import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../context/NotificationContext';
import { AppNotification } from '../services/api';
import { Bell } from 'lucide-react';
import { HeaderEmojiButton } from './layout/NavMenu';
import { formatCompanyDate } from '../utils/companyDateFormat';

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return formatCompanyDate(dateStr);
}

function notificationIcon(type: string): string {
  if (type.startsWith('Holiday') || type.startsWith('SickLeave')) return '🏖️';
  if (type.startsWith('Task') || type.startsWith('SubTask')) return '✅';
  if (type.startsWith('Shift') || type.startsWith('Schedule')) return '📅';
  if (type.startsWith('Attendance')) return '⏰';
  if (type.startsWith('Timecard')) return '📝';
  if (type.startsWith('Incident')) return '⚠️';
  if (type.startsWith('Training')) return '🎓';
  if (type.startsWith('License')) return '📋';
  if (type.startsWith('Document')) return '📄';
  if (type.startsWith('Feedback') || type.startsWith('Pay')) return '💼';
  return '🔔';
}

const NotificationPanel: React.FC = () => {
  const navigate = useNavigate();
  const {
    unreadCount,
    notifications,
    totalNotifications,
    loading,
    loadNotifications,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();

  const [isOpen, setIsOpen] = useState(false);
  const [page, setPage] = useState(1);
  const panelRef = useRef<HTMLDivElement>(null);

  // Close when clicking outside
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const handleOpen = useCallback(() => {
    setIsOpen(prev => !prev);
    if (!isOpen) {
      setPage(1);
      loadNotifications(1);
    }
  }, [isOpen, loadNotifications]);

  const handleNotificationClick = useCallback(async (notification: AppNotification) => {
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }
    setIsOpen(false);
    if (notification.deepLinkWeb) {
      navigate(notification.deepLinkWeb);
    }
  }, [markAsRead, navigate]);

  const handleLoadMore = useCallback(async () => {
    const next = page + 1;
    setPage(next);
    await loadNotifications(next);
  }, [page, loadNotifications]);

  const hasMore = notifications.length < totalNotifications;

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell Button */}
      <HeaderEmojiButton
        icon={<Bell className="h-5 w-5" strokeWidth={1.75} />}
        label="Notifications"
        unreadCount={unreadCount}
        onClick={handleOpen}
      />

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-neutral-200 z-50 flex flex-col"
          style={{ maxHeight: '520px' }}>
          
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 flex-shrink-0">
            <h3 className="text-sm font-semibold text-neutral-800">
              Notifications {unreadCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded-full">{unreadCount}</span>
              )}
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllAsRead()}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notification List */}
          <div className="overflow-y-auto flex-1">
            {loading && notifications.length === 0 ? (
              <div className="flex items-center justify-center py-10 text-neutral-400 text-sm">
                Loading...
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-neutral-400">
                <span className="text-3xl mb-2">🔔</span>
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : (
              <>
                {notifications.map(notif => (
                  <div
                    key={notif.id}
                    onClick={() => handleNotificationClick(notif)}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-neutral-100 cursor-pointer transition-colors hover:bg-neutral-50
                      ${!notif.isRead ? 'bg-blue-50 hover:bg-blue-100' : ''}`}
                  >
                    <span className="text-lg flex-shrink-0 mt-0.5">{notificationIcon(notif.type)}</span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm leading-snug ${!notif.isRead ? 'font-semibold text-neutral-900' : 'text-neutral-700'}`}>
                        {notif.title}
                      </p>
                      <p className="text-xs text-neutral-500 mt-0.5 leading-snug line-clamp-2">
                        {notif.message}
                      </p>
                      <p className="text-xs text-neutral-400 mt-1">{timeAgo(notif.createdAt)}</p>
                      {!notif.isRead ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            void markAsRead(notif.id);
                          }}
                          className="text-left text-xs text-blue-600 hover:text-blue-800 font-medium mt-0.5"
                        >
                          Mark as read
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            void markAsUnread(notif.id);
                          }}
                          className="text-left text-xs text-blue-600 hover:text-blue-800 font-medium mt-0.5"
                        >
                          Mark as unread
                        </button>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      {!notif.isRead && (
                        <span className="w-2 h-2 rounded-full bg-blue-500 mt-1" />
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteNotification(notif.id); }}
                        className="text-neutral-300 hover:text-red-400 transition-colors text-xs leading-none"
                        title="Dismiss"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}

                {hasMore && (
                  <div className="px-4 py-3 text-center">
                    <button
                      onClick={handleLoadMore}
                      disabled={loading}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
                    >
                      {loading ? 'Loading...' : 'Load more'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;
